import type { Theme } from '@mui/material/styles';
import type { SxProps } from '@mui/material';

const SCROLLBAR_SIZE = 12;

function scrollbarColors(theme: Theme) {
  const isDark = theme.palette.mode === 'dark';
  return {
    track: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
    thumb: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
    thumbHover: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
  };
}

/** Global scrollbar styling via MuiCssBaseline `body` (vertical + horizontal). */
export function getScrollbarCssBaselineBodyStyles(theme: Theme): Record<string, unknown> {
  const { track, thumb, thumbHover } = scrollbarColors(theme);

  return {
    scrollbarWidth: 'thin',
    scrollbarColor: `${thumb} ${track}`,
    '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
      width: SCROLLBAR_SIZE,
      height: SCROLLBAR_SIZE,
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
      backgroundColor: track,
    },
    '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
      backgroundColor: thumb,
      borderRadius: 6,
      minHeight: 24,
      minWidth: 24,
      '&:hover': {
        backgroundColor: thumbHover,
      },
      '&:active': {
        backgroundColor: thumbHover,
      },
    },
    '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
      backgroundColor: track,
    },
  };
}

/** Per-container scrollbar styling (tables, panels, etc.). */
export function scrollbarSx(theme: Theme): SxProps<Theme> {
  const { track, thumb, thumbHover } = scrollbarColors(theme);

  return {
    scrollbarWidth: 'thin',
    scrollbarColor: `${thumb} ${track}`,
    '&::-webkit-scrollbar': {
      width: SCROLLBAR_SIZE,
      height: SCROLLBAR_SIZE,
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: track,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: thumb,
      borderRadius: '6px',
      minHeight: 24,
      minWidth: 24,
      '&:hover': {
        backgroundColor: thumbHover,
      },
      '&:active': {
        backgroundColor: thumbHover,
      },
    },
    '&::-webkit-scrollbar-corner': {
      backgroundColor: track,
    },
  };
}
